import { useEffect, useState } from 'react'
import {
  Title, Group, Card, Stack, Text, TextInput, Textarea, Button,
  Table, Badge, ActionIcon, Modal, LoadingOverlay, Tabs, NumberInput, Tooltip,
} from '@mantine/core'
import { IconPlus, IconPencil, IconTrash, IconEye, IconEyeOff, IconPhoto, IconUsers, IconStar, IconHistory, IconCertificate, IconBuildingStore, IconInfoCircle, IconArrowUp, IconArrowDown, IconDeviceFloppy } from '@tabler/icons-react'
import { AboutTab } from '@/pages/website/index'
import { useForm, Controller } from 'react-hook-form'
import toast from 'react-hot-toast'
import { api } from '@/lib/api'
import { confirmDelete, confirmAction } from '@/lib/confirm'
import { useBoard, useValues, useMilestones, useCertifications, usePartners } from '@/lib/swr'
import type { BoardMember, ValueItem, Milestone, Certification, Partner } from '@/types'

export default function AboutPage() {
  return (
    <Stack>
      <Title order={2}>Бидний тухай</Title>
      <Card withBorder>
        <Tabs defaultValue="info">
          <Tabs.List>
            <Tabs.Tab value="info" leftSection={<IconInfoCircle size={14} />}>Үндсэн мэдээлэл</Tabs.Tab>
            <Tabs.Tab value="board" leftSection={<IconUsers size={14} />}>Удирдлага</Tabs.Tab>
            <Tabs.Tab value="values" leftSection={<IconStar size={14} />}>Үнэт зүйл</Tabs.Tab>
            <Tabs.Tab value="milestones" leftSection={<IconHistory size={14} />}>Түүх</Tabs.Tab>
            <Tabs.Tab value="certifications" leftSection={<IconCertificate size={14} />}>Гэрчилгээ</Tabs.Tab>
            <Tabs.Tab value="partners" leftSection={<IconBuildingStore size={14} />}>Хамтрагч</Tabs.Tab>
          </Tabs.List>
          <Tabs.Panel value="info" pt="md"><AboutTab /></Tabs.Panel>
          <Tabs.Panel value="board" pt="md"><BoardEditor /></Tabs.Panel>
          <Tabs.Panel value="values" pt="md"><ValuesEditor /></Tabs.Panel>
          <Tabs.Panel value="milestones" pt="md"><MilestonesEditor /></Tabs.Panel>
          <Tabs.Panel value="certifications" pt="md"><CertificationsEditor /></Tabs.Panel>
          <Tabs.Panel value="partners" pt="md"><PartnersEditor /></Tabs.Panel>
        </Tabs>
      </Card>
    </Stack>
  )
}

function pickFile(onFile: (file: File) => void) {
  const inp = document.createElement('input')
  inp.type = 'file'
  inp.accept = 'image/*'
  inp.onchange = () => inp.files?.[0] && onFile(inp.files[0])
  inp.click()
}

// ─── Board Editor ───────────────────────────────────────────────────────────

interface BoardFormData { name: string; role: string; quote: string; credentials: string; image: string; order: number }
const boardEmpty: BoardFormData = { name: '', role: '', quote: '', credentials: '', image: '', order: 0 }

function BoardEditor() {
  const { data, isLoading, mutate } = useBoard()
  const [items, setItems] = useState<BoardMember[]>([])
  const [orderChanged, setOrderChanged] = useState(false)
  const [reordering, setReordering] = useState(false)
  useEffect(() => {
    const list: BoardMember[] = data?.data ?? []
    setItems([...list].sort((a, b) => (a.order ?? 0) - (b.order ?? 0)))
    setOrderChanged(false)
  }, [data])
  const moveUp = (index: number) => {
    if (index === 0) return
    setItems((prev) => { const next = [...prev]; [next[index - 1], next[index]] = [next[index], next[index - 1]]; return next })
    setOrderChanged(true)
  }
  const moveDown = (index: number) => {
    if (index === items.length - 1) return
    setItems((prev) => { const next = [...prev]; [next[index], next[index + 1]] = [next[index + 1], next[index]]; return next })
    setOrderChanged(true)
  }
  const saveOrder = () => confirmAction('Шинэ дарааллыг хадгалах уу? Энэ нь website дээр харагдах дарааллыг өөрчилнө.', async () => {
    setReordering(true)
    try {
      await api.admin.reorderBoard(items.map((i) => i._id))
      toast.success('Дараалал хадгалагдлаа')
      setOrderChanged(false); mutate()
    } catch (e: any) {
      toast.error(e instanceof Error ? e.message : 'Алдаа гарлаа')
    } finally { setReordering(false) }
  }, { title: 'Дараалал хадгалах', confirmLabel: 'Хадгалах' })

  const [open, setOpen] = useState(false)
  const [editId, setEditId] = useState<string | null>(null)
  const [uploading, setUploading] = useState(false)

  const { control, handleSubmit, reset, setValue, watch, formState: { errors, isSubmitting } } =
    useForm<BoardFormData>({ defaultValues: boardEmpty })
  const watchImage = watch('image')

  const create = () => { setEditId(null); reset(boardEmpty); setOpen(true) }
  const edit = (i: BoardMember) => {
    setEditId(i._id)
    reset({ name: i.name, role: i.role, quote: i.quote, credentials: i.credentials, image: i.image, order: i.order })
    setOpen(true)
  }
  const close = () => { setOpen(false); reset(boardEmpty) }
  const onSubmit = handleSubmit(async (form) => {
    try {
      if (editId) await api.admin.updateBoard(editId, form as unknown as Record<string, unknown>)
      else await api.admin.createBoard(form as unknown as Record<string, unknown>)
      toast.success(editId ? 'Шинэчлэгдлээ' : 'Нэмэгдлээ')
      close(); mutate()
    } catch (e: any) { toast.error(e instanceof Error ? e.message : 'Алдаа гарлаа') }
  })
  const remove = (id: string) => confirmDelete('Энэ бичлэгийг устгах уу?', async () => {
    try { await api.admin.deleteBoard(id); mutate() }
    catch (e: any) { toast.error(e instanceof Error ? e.message : 'Алдаа гарлаа') }
  })
  const toggle = async (i: BoardMember) => {
    try { await api.admin.updateBoardStatus(i._id, !i.isActive); mutate() }
    catch (e: any) { toast.error(e instanceof Error ? e.message : 'Алдаа гарлаа') }
  }
  const upload = async (file: File) => {
    setUploading(true)
    try { const r = await api.admin.uploadImage(file); setValue('image', r.url, { shouldDirty: true }) }
    catch { toast.error('Зураг алдаа') }
    finally { setUploading(false) }
  }

  return (
    <Stack>
      <Group justify="space-between"><Text fw={500}>Удирдлагын баг ({items.length})</Text>
        <Group gap="xs">
          {orderChanged && (
            <Button size="xs" variant="light" color="orange" leftSection={<IconDeviceFloppy size={14} />} onClick={saveOrder} loading={reordering}>Дараалал хадгалах</Button>
          )}
          <Button size="xs" leftSection={<IconPlus size={14} />} onClick={create}>Нэмэх</Button>
        </Group>
      </Group>
      <div style={{ position: 'relative' }}>
        <LoadingOverlay visible={isLoading} />
        <Table striped>
          <Table.Thead><Table.Tr><Table.Th>Зураг</Table.Th><Table.Th>Нэр</Table.Th><Table.Th>Албан тушаал</Table.Th><Table.Th>Статус</Table.Th><Table.Th w={170}>Үйлдэл</Table.Th></Table.Tr></Table.Thead>
          <Table.Tbody>
            {items.map((i, idx) => (
              <Table.Tr key={i._id}>
                <Table.Td>{i.image ? <img src={i.image} alt="" style={{ width: 36, height: 36, borderRadius: '50%', objectFit: 'cover' }} /> : '—'}</Table.Td>
                <Table.Td><Text size="sm" fw={600}>{i.name}</Text><Text size="xs" c="dimmed">{i.credentials}</Text></Table.Td>
                <Table.Td><Text size="xs">{i.role}</Text></Table.Td>
                <Table.Td><Badge color={i.isActive ? 'green' : 'gray'} variant="light" size="sm">{i.isActive ? 'Идэвхтэй' : 'Идэвхгүй'}</Badge></Table.Td>
                <Table.Td><Group gap={4}>
                  <Tooltip label="Дээш" position="top" withArrow><ActionIcon variant="subtle" color="gray" size="sm" disabled={idx === 0} onClick={() => moveUp(idx)}><IconArrowUp size={14} /></ActionIcon></Tooltip>
                  <Tooltip label="Доош" position="top" withArrow><ActionIcon variant="subtle" color="gray" size="sm" disabled={idx === items.length - 1} onClick={() => moveDown(idx)}><IconArrowDown size={14} /></ActionIcon></Tooltip>
                  <ActionIcon variant="light" size="sm" onClick={() => toggle(i)}>{i.isActive ? <IconEye size={14} /> : <IconEyeOff size={14} />}</ActionIcon>
                  <ActionIcon variant="light" size="sm" onClick={() => edit(i)}><IconPencil size={14} /></ActionIcon>
                  <ActionIcon variant="light" color="red" size="sm" onClick={() => remove(i._id)}><IconTrash size={14} /></ActionIcon>
                </Group></Table.Td>
              </Table.Tr>
            ))}
            {items.length === 0 && !isLoading && <Table.Tr><Table.Td colSpan={5}><Text ta="center" c="dimmed" py="lg">Хоосон</Text></Table.Td></Table.Tr>}
          </Table.Tbody>
        </Table>
      </div>

      <Modal opened={open} onClose={close} title={editId ? 'Засах' : 'Шинэ'} size="lg">
        <form onSubmit={onSubmit}>
          <Stack>
            <Controller name="name" control={control} rules={{ validate: (v) => v.trim() ? true : 'Нэр шаардлагатай' }} render={({ field }) => <TextInput label="Нэр *" {...field} error={errors.name?.message} />} />
            <Controller name="role" control={control} render={({ field }) => <TextInput label="Албан тушаал" {...field} />} />
            <Controller name="credentials" control={control} render={({ field }) => <TextInput label="Боловсрол / Зэрэг" {...field} />} />
            <Controller name="quote" control={control} render={({ field }) => <Textarea label="Иш татах үг" {...field} minRows={3} />} />
            <Controller name="order" control={control} render={({ field }) => <NumberInput label="Дараалал" value={field.value} onChange={(v) => field.onChange(Number(v) || 0)} min={0} />} />
            <div>
              <Text size="sm" fw={500} mb={4}>Зураг</Text>
              <Group>
                <Controller name="image" control={control} render={({ field }) => <TextInput flex={1} placeholder="URL" {...field} />} />
                <Button size="sm" variant="light" leftSection={<IconPhoto size={14} />} loading={uploading} onClick={() => pickFile(upload)}>Upload</Button>
              </Group>
              {watchImage && <img src={watchImage} alt="" style={{ marginTop: 8, maxHeight: 80, borderRadius: 8 }} />}
            </div>
            <Group justify="flex-end">
              <Button variant="light" onClick={close} disabled={isSubmitting}>Болих</Button>
              <Button type="submit" loading={isSubmitting} disabled={isSubmitting}>{editId ? 'Хадгалах' : 'Нэмэх'}</Button>
            </Group>
          </Stack>
        </form>
      </Modal>
    </Stack>
  )
}

// ─── Values Editor ──────────────────────────────────────────────────────────

interface ValueFormData { title: string; description: string; icon: string; order: number }
const valueEmpty: ValueFormData = { title: '', description: '', icon: '', order: 0 }

function ValuesEditor() {
  const { data, isLoading, mutate } = useValues()
  const [items, setItems] = useState<ValueItem[]>([])
  const [orderChanged, setOrderChanged] = useState(false)
  const [reordering, setReordering] = useState(false)
  useEffect(() => {
    const list: ValueItem[] = data?.data ?? []
    setItems([...list].sort((a, b) => (a.order ?? 0) - (b.order ?? 0)))
    setOrderChanged(false)
  }, [data])
  const moveUp = (index: number) => {
    if (index === 0) return
    setItems((prev) => { const next = [...prev]; [next[index - 1], next[index]] = [next[index], next[index - 1]]; return next })
    setOrderChanged(true)
  }
  const moveDown = (index: number) => {
    if (index === items.length - 1) return
    setItems((prev) => { const next = [...prev]; [next[index], next[index + 1]] = [next[index + 1], next[index]]; return next })
    setOrderChanged(true)
  }
  const saveOrder = () => confirmAction('Шинэ дарааллыг хадгалах уу? Энэ нь website дээр харагдах дарааллыг өөрчилнө.', async () => {
    setReordering(true)
    try {
      await api.admin.reorderValues(items.map((i) => i._id))
      toast.success('Дараалал хадгалагдлаа')
      setOrderChanged(false); mutate()
    } catch (e: any) { toast.error(e instanceof Error ? e.message : 'Алдаа гарлаа') }
    finally { setReordering(false) }
  }, { title: 'Дараалал хадгалах', confirmLabel: 'Хадгалах' })

  const [open, setOpen] = useState(false)
  const [editId, setEditId] = useState<string | null>(null)
  const [uploading, setUploading] = useState(false)
  const { control, handleSubmit, reset, setValue, watch, formState: { errors, isSubmitting } } =
    useForm<ValueFormData>({ defaultValues: valueEmpty })
  const watchIcon = watch('icon')

  const create = () => { setEditId(null); reset(valueEmpty); setOpen(true) }
  const edit = (i: ValueItem) => {
    setEditId(i._id)
    reset({ title: i.title, description: i.description, icon: i.icon, order: i.order })
    setOpen(true)
  }
  const close = () => { setOpen(false); reset(valueEmpty) }
  const onSubmit = handleSubmit(async (form) => {
    try {
      if (editId) await api.admin.updateValue(editId, form as unknown as Record<string, unknown>)
      else await api.admin.createValue(form as unknown as Record<string, unknown>)
      close(); mutate()
    } catch (e: any) { toast.error(e instanceof Error ? e.message : 'Алдаа гарлаа') }
  })
  const remove = (id: string) => confirmDelete('Энэ бичлэгийг устгах уу?', async () => { await api.admin.deleteValue(id); mutate() })
  const toggle = async (i: ValueItem) => { await api.admin.updateValueStatus(i._id, !i.isActive); mutate() }
  const upload = async (file: File) => {
    setUploading(true)
    try { const r = await api.admin.uploadImage(file); setValue('icon', r.url, { shouldDirty: true }) }
    finally { setUploading(false) }
  }

  return (
    <Stack>
      <Group justify="space-between"><Text fw={500}>Үнэт зүйлс ({items.length})</Text>
        <Group gap="xs">
          {orderChanged && (
            <Button size="xs" variant="light" color="orange" leftSection={<IconDeviceFloppy size={14} />} onClick={saveOrder} loading={reordering}>Дараалал хадгалах</Button>
          )}
          <Button size="xs" leftSection={<IconPlus size={14} />} onClick={create}>Нэмэх</Button>
        </Group>
      </Group>
      <div style={{ position: 'relative' }}>
        <LoadingOverlay visible={isLoading} />
        <Table striped>
          <Table.Thead><Table.Tr><Table.Th>Гарчиг</Table.Th><Table.Th>Тайлбар</Table.Th><Table.Th>Статус</Table.Th><Table.Th w={170}>Үйлдэл</Table.Th></Table.Tr></Table.Thead>
          <Table.Tbody>
            {items.map((i, idx) => (
              <Table.Tr key={i._id}>
                <Table.Td><Text size="sm" fw={600}>{i.title}</Text></Table.Td>
                <Table.Td><Text size="xs" c="dimmed" lineClamp={2}>{i.description}</Text></Table.Td>
                <Table.Td><Badge color={i.isActive ? 'green' : 'gray'} variant="light" size="sm">{i.isActive ? 'Идэвхтэй' : 'Идэвхгүй'}</Badge></Table.Td>
                <Table.Td><Group gap={4}>
                  <Tooltip label="Дээш" position="top" withArrow><ActionIcon variant="subtle" color="gray" size="sm" disabled={idx === 0} onClick={() => moveUp(idx)}><IconArrowUp size={14} /></ActionIcon></Tooltip>
                  <Tooltip label="Доош" position="top" withArrow><ActionIcon variant="subtle" color="gray" size="sm" disabled={idx === items.length - 1} onClick={() => moveDown(idx)}><IconArrowDown size={14} /></ActionIcon></Tooltip>
                  <ActionIcon variant="light" size="sm" onClick={() => toggle(i)}>{i.isActive ? <IconEye size={14} /> : <IconEyeOff size={14} />}</ActionIcon>
                  <ActionIcon variant="light" size="sm" onClick={() => edit(i)}><IconPencil size={14} /></ActionIcon>
                  <ActionIcon variant="light" color="red" size="sm" onClick={() => remove(i._id)}><IconTrash size={14} /></ActionIcon>
                </Group></Table.Td>
              </Table.Tr>
            ))}
            {items.length === 0 && !isLoading && <Table.Tr><Table.Td colSpan={4}><Text ta="center" c="dimmed" py="lg">Хоосон</Text></Table.Td></Table.Tr>}
          </Table.Tbody>
        </Table>
      </div>
      <Modal opened={open} onClose={close} title={editId ? 'Засах' : 'Шинэ'} size="lg">
        <form onSubmit={onSubmit}>
          <Stack>
            <Controller name="title" control={control} rules={{ validate: (v) => v.trim() ? true : 'Гарчиг шаардлагатай' }} render={({ field }) => <TextInput label="Гарчиг *" {...field} error={errors.title?.message} />} />
            <Controller name="description" control={control} render={({ field }) => <Textarea label="Тайлбар" {...field} minRows={3} />} />
            <div>
              <Text size="sm" fw={500} mb={4}>Icon</Text>
              <Group>
                <Controller name="icon" control={control} render={({ field }) => <TextInput flex={1} placeholder="URL" {...field} />} />
                <Button size="sm" variant="light" leftSection={<IconPhoto size={14} />} loading={uploading} onClick={() => pickFile(upload)}>Upload</Button>
              </Group>
              {watchIcon && <img src={watchIcon} alt="" style={{ marginTop: 8, maxHeight: 60 }} />}
            </div>
            <Controller name="order" control={control} render={({ field }) => <NumberInput label="Дараалал" value={field.value} onChange={(v) => field.onChange(Number(v) || 0)} min={0} />} />
            <Group justify="flex-end"><Button variant="light" onClick={close} disabled={isSubmitting}>Болих</Button><Button type="submit" loading={isSubmitting} disabled={isSubmitting}>{editId ? 'Хадгалах' : 'Нэмэх'}</Button></Group>
          </Stack>
        </form>
      </Modal>
    </Stack>
  )
}

// ─── Milestones Editor ──────────────────────────────────────────────────────

interface MilestoneFormData { year: string; title: string; description: string; order: number }
const milestoneEmpty: MilestoneFormData = { year: '', title: '', description: '', order: 0 }

function MilestonesEditor() {
  const { data, isLoading, mutate } = useMilestones()
  const [items, setItems] = useState<Milestone[]>([])
  const [orderChanged, setOrderChanged] = useState(false)
  const [reordering, setReordering] = useState(false)
  useEffect(() => {
    const list: Milestone[] = data?.data ?? []
    setItems([...list].sort((a, b) => (a.order ?? 0) - (b.order ?? 0)))
    setOrderChanged(false)
  }, [data])
  const moveUp = (index: number) => {
    if (index === 0) return
    setItems((prev) => { const next = [...prev]; [next[index - 1], next[index]] = [next[index], next[index - 1]]; return next })
    setOrderChanged(true)
  }
  const moveDown = (index: number) => {
    if (index === items.length - 1) return
    setItems((prev) => { const next = [...prev]; [next[index], next[index + 1]] = [next[index + 1], next[index]]; return next })
    setOrderChanged(true)
  }
  const saveOrder = () => confirmAction('Шинэ дарааллыг хадгалах уу? Энэ нь website дээр харагдах дарааллыг өөрчилнө.', async () => {
    setReordering(true)
    try {
      await api.admin.reorderMilestones(items.map((i) => i._id))
      toast.success('Дараалал хадгалагдлаа')
      setOrderChanged(false); mutate()
    } catch (e: any) { toast.error(e instanceof Error ? e.message : 'Алдаа гарлаа') }
    finally { setReordering(false) }
  }, { title: 'Дараалал хадгалах', confirmLabel: 'Хадгалах' })

  const [open, setOpen] = useState(false)
  const [editId, setEditId] = useState<string | null>(null)
  const { control, handleSubmit, reset, formState: { errors, isSubmitting } } =
    useForm<MilestoneFormData>({ defaultValues: milestoneEmpty })

  const create = () => { setEditId(null); reset(milestoneEmpty); setOpen(true) }
  const edit = (i: Milestone) => {
    setEditId(i._id)
    reset({ year: i.year, title: i.title, description: i.description, order: i.order })
    setOpen(true)
  }
  const close = () => { setOpen(false); reset(milestoneEmpty) }
  const onSubmit = handleSubmit(async (form) => {
    try {
      if (editId) await api.admin.updateMilestone(editId, form as unknown as Record<string, unknown>)
      else await api.admin.createMilestone(form as unknown as Record<string, unknown>)
      close(); mutate()
    } catch (e: any) { toast.error(e instanceof Error ? e.message : 'Алдаа гарлаа') }
  })
  const remove = (id: string) => confirmDelete('Энэ бичлэгийг устгах уу?', async () => { await api.admin.deleteMilestone(id); mutate() })
  const toggle = async (i: Milestone) => { await api.admin.updateMilestoneStatus(i._id, !i.isActive); mutate() }

  return (
    <Stack>
      <Group justify="space-between"><Text fw={500}>Түүхэн үе ({items.length})</Text>
        <Group gap="xs">
          {orderChanged && (
            <Button size="xs" variant="light" color="orange" leftSection={<IconDeviceFloppy size={14} />} onClick={saveOrder} loading={reordering}>Дараалал хадгалах</Button>
          )}
          <Button size="xs" leftSection={<IconPlus size={14} />} onClick={create}>Нэмэх</Button>
        </Group>
      </Group>
      <div style={{ position: 'relative' }}>
        <LoadingOverlay visible={isLoading} />
        <Table striped>
          <Table.Thead><Table.Tr><Table.Th w={80}>Он</Table.Th><Table.Th>Гарчиг</Table.Th><Table.Th>Тайлбар</Table.Th><Table.Th>Статус</Table.Th><Table.Th w={170}>Үйлдэл</Table.Th></Table.Tr></Table.Thead>
          <Table.Tbody>
            {items.map((i, idx) => (
              <Table.Tr key={i._id}>
                <Table.Td><Text size="sm" fw={700}>{i.year}</Text></Table.Td>
                <Table.Td><Text size="sm" fw={600}>{i.title}</Text></Table.Td>
                <Table.Td><Text size="xs" c="dimmed" lineClamp={2}>{i.description}</Text></Table.Td>
                <Table.Td><Badge color={i.isActive ? 'green' : 'gray'} variant="light" size="sm">{i.isActive ? 'Идэвхтэй' : 'Идэвхгүй'}</Badge></Table.Td>
                <Table.Td><Group gap={4}>
                  <Tooltip label="Дээш" position="top" withArrow><ActionIcon variant="subtle" color="gray" size="sm" disabled={idx === 0} onClick={() => moveUp(idx)}><IconArrowUp size={14} /></ActionIcon></Tooltip>
                  <Tooltip label="Доош" position="top" withArrow><ActionIcon variant="subtle" color="gray" size="sm" disabled={idx === items.length - 1} onClick={() => moveDown(idx)}><IconArrowDown size={14} /></ActionIcon></Tooltip>
                  <ActionIcon variant="light" size="sm" onClick={() => toggle(i)}>{i.isActive ? <IconEye size={14} /> : <IconEyeOff size={14} />}</ActionIcon>
                  <ActionIcon variant="light" size="sm" onClick={() => edit(i)}><IconPencil size={14} /></ActionIcon>
                  <ActionIcon variant="light" color="red" size="sm" onClick={() => remove(i._id)}><IconTrash size={14} /></ActionIcon>
                </Group></Table.Td>
              </Table.Tr>
            ))}
            {items.length === 0 && !isLoading && <Table.Tr><Table.Td colSpan={5}><Text ta="center" c="dimmed" py="lg">Хоосон</Text></Table.Td></Table.Tr>}
          </Table.Tbody>
        </Table>
      </div>
      <Modal opened={open} onClose={close} title={editId ? 'Засах' : 'Шинэ'} size="lg">
        <form onSubmit={onSubmit}>
          <Stack>
            <Group grow>
              <Controller name="year" control={control} rules={{ validate: (v) => v.trim() ? true : 'Он шаардлагатай' }} render={({ field }) => <TextInput label="Он *" {...field} error={errors.year?.message} />} />
              <Controller name="title" control={control} rules={{ validate: (v) => v.trim() ? true : 'Гарчиг шаардлагатай' }} render={({ field }) => <TextInput label="Гарчиг *" {...field} error={errors.title?.message} />} />
            </Group>
            <Controller name="description" control={control} render={({ field }) => <Textarea label="Тайлбар" {...field} minRows={3} />} />
            <Controller name="order" control={control} render={({ field }) => <NumberInput label="Дараалал" value={field.value} onChange={(v) => field.onChange(Number(v) || 0)} min={0} />} />
            <Group justify="flex-end"><Button variant="light" onClick={close} disabled={isSubmitting}>Болих</Button><Button type="submit" loading={isSubmitting} disabled={isSubmitting}>{editId ? 'Хадгалах' : 'Нэмэх'}</Button></Group>
          </Stack>
        </form>
      </Modal>
    </Stack>
  )
}

// ─── Certifications Editor ──────────────────────────────────────────────────

interface CertFormData { name: string; description: string; image: string; order: number }
const certEmpty: CertFormData = { name: '', description: '', image: '', order: 0 }

function CertificationsEditor() {
  const { data, isLoading, mutate } = useCertifications()
  const [items, setItems] = useState<Certification[]>([])
  const [orderChanged, setOrderChanged] = useState(false)
  const [reordering, setReordering] = useState(false)
  useEffect(() => {
    const list: Certification[] = data?.data ?? []
    setItems([...list].sort((a, b) => (a.order ?? 0) - (b.order ?? 0)))
    setOrderChanged(false)
  }, [data])
  const moveUp = (index: number) => {
    if (index === 0) return
    setItems((prev) => { const next = [...prev]; [next[index - 1], next[index]] = [next[index], next[index - 1]]; return next })
    setOrderChanged(true)
  }
  const moveDown = (index: number) => {
    if (index === items.length - 1) return
    setItems((prev) => { const next = [...prev]; [next[index], next[index + 1]] = [next[index + 1], next[index]]; return next })
    setOrderChanged(true)
  }
  const saveOrder = () => confirmAction('Шинэ дарааллыг хадгалах уу? Энэ нь website дээр харагдах дарааллыг өөрчилнө.', async () => {
    setReordering(true)
    try {
      await api.admin.reorderCertifications(items.map((i) => i._id))
      toast.success('Дараалал хадгалагдлаа')
      setOrderChanged(false); mutate()
    } catch (e: any) { toast.error(e instanceof Error ? e.message : 'Алдаа гарлаа') }
    finally { setReordering(false) }
  }, { title: 'Дараалал хадгалах', confirmLabel: 'Хадгалах' })

  const [open, setOpen] = useState(false)
  const [editId, setEditId] = useState<string | null>(null)
  const [uploading, setUploading] = useState(false)
  const { control, handleSubmit, reset, setValue, watch, formState: { errors, isSubmitting } } =
    useForm<CertFormData>({ defaultValues: certEmpty })
  const watchImage = watch('image')

  const create = () => { setEditId(null); reset(certEmpty); setOpen(true) }
  const edit = (i: Certification) => {
    setEditId(i._id)
    reset({ name: i.name, description: i.description, image: i.image, order: i.order })
    setOpen(true)
  }
  const close = () => { setOpen(false); reset(certEmpty) }
  const onSubmit = handleSubmit(async (form) => {
    try {
      if (editId) await api.admin.updateCertification(editId, form as unknown as Record<string, unknown>)
      else await api.admin.createCertification(form as unknown as Record<string, unknown>)
      close(); mutate()
    } catch (e: any) { toast.error(e instanceof Error ? e.message : 'Алдаа гарлаа') }
  })
  const remove = (id: string) => confirmDelete('Энэ бичлэгийг устгах уу?', async () => { await api.admin.deleteCertification(id); mutate() })
  const toggle = async (i: Certification) => { await api.admin.updateCertificationStatus(i._id, !i.isActive); mutate() }
  const upload = async (file: File) => {
    setUploading(true)
    try { const r = await api.admin.uploadImage(file); setValue('image', r.url, { shouldDirty: true }) }
    finally { setUploading(false) }
  }

  return (
    <Stack>
      <Group justify="space-between"><Text fw={500}>Гэрчилгээ ({items.length})</Text>
        <Group gap="xs">
          {orderChanged && (
            <Button size="xs" variant="light" color="orange" leftSection={<IconDeviceFloppy size={14} />} onClick={saveOrder} loading={reordering}>Дараалал хадгалах</Button>
          )}
          <Button size="xs" leftSection={<IconPlus size={14} />} onClick={create}>Нэмэх</Button>
        </Group>
      </Group>
      <div style={{ position: 'relative' }}>
        <LoadingOverlay visible={isLoading} />
        <Table striped>
          <Table.Thead><Table.Tr><Table.Th>Зураг</Table.Th><Table.Th>Нэр</Table.Th><Table.Th>Тайлбар</Table.Th><Table.Th>Статус</Table.Th><Table.Th w={170}>Үйлдэл</Table.Th></Table.Tr></Table.Thead>
          <Table.Tbody>
            {items.map((i, idx) => (
              <Table.Tr key={i._id}>
                <Table.Td>{i.image ? <img src={i.image} alt="" style={{ width: 48, height: 36, objectFit: 'cover', borderRadius: 4 }} /> : '—'}</Table.Td>
                <Table.Td><Text size="sm" fw={600}>{i.name}</Text></Table.Td>
                <Table.Td><Text size="xs" c="dimmed" lineClamp={2}>{i.description}</Text></Table.Td>
                <Table.Td><Badge color={i.isActive ? 'green' : 'gray'} variant="light" size="sm">{i.isActive ? 'Идэвхтэй' : 'Идэвхгүй'}</Badge></Table.Td>
                <Table.Td><Group gap={4}>
                  <Tooltip label="Дээш" position="top" withArrow><ActionIcon variant="subtle" color="gray" size="sm" disabled={idx === 0} onClick={() => moveUp(idx)}><IconArrowUp size={14} /></ActionIcon></Tooltip>
                  <Tooltip label="Доош" position="top" withArrow><ActionIcon variant="subtle" color="gray" size="sm" disabled={idx === items.length - 1} onClick={() => moveDown(idx)}><IconArrowDown size={14} /></ActionIcon></Tooltip>
                  <ActionIcon variant="light" size="sm" onClick={() => toggle(i)}>{i.isActive ? <IconEye size={14} /> : <IconEyeOff size={14} />}</ActionIcon>
                  <ActionIcon variant="light" size="sm" onClick={() => edit(i)}><IconPencil size={14} /></ActionIcon>
                  <ActionIcon variant="light" color="red" size="sm" onClick={() => remove(i._id)}><IconTrash size={14} /></ActionIcon>
                </Group></Table.Td>
              </Table.Tr>
            ))}
            {items.length === 0 && !isLoading && <Table.Tr><Table.Td colSpan={5}><Text ta="center" c="dimmed" py="lg">Хоосон</Text></Table.Td></Table.Tr>}
          </Table.Tbody>
        </Table>
      </div>
      <Modal opened={open} onClose={close} title={editId ? 'Засах' : 'Шинэ'} size="lg">
        <form onSubmit={onSubmit}>
          <Stack>
            <Controller name="name" control={control} rules={{ validate: (v) => v.trim() ? true : 'Нэр шаардлагатай' }} render={({ field }) => <TextInput label="Нэр *" {...field} error={errors.name?.message} />} />
            <Controller name="description" control={control} render={({ field }) => <Textarea label="Тайлбар" {...field} minRows={3} />} />
            <Controller name="order" control={control} render={({ field }) => <NumberInput label="Дараалал" value={field.value} onChange={(v) => field.onChange(Number(v) || 0)} min={0} />} />
            <div>
              <Text size="sm" fw={500} mb={4}>Зураг</Text>
              <Group>
                <Controller name="image" control={control} render={({ field }) => <TextInput flex={1} placeholder="URL" {...field} />} />
                <Button size="sm" variant="light" leftSection={<IconPhoto size={14} />} loading={uploading} onClick={() => pickFile(upload)}>Upload</Button>
              </Group>
              {watchImage && <img src={watchImage} alt="" style={{ marginTop: 8, maxHeight: 80, borderRadius: 8 }} />}
            </div>
            <Group justify="flex-end"><Button variant="light" onClick={close} disabled={isSubmitting}>Болих</Button><Button type="submit" loading={isSubmitting} disabled={isSubmitting}>{editId ? 'Хадгалах' : 'Нэмэх'}</Button></Group>
          </Stack>
        </form>
      </Modal>
    </Stack>
  )
}

// ─── Partners Editor ────────────────────────────────────────────────────────

interface PartnerFormData { name: string; logo: string; url: string; order: number }
const partnerEmpty: PartnerFormData = { name: '', logo: '', url: '', order: 0 }

function PartnersEditor() {
  const { data, isLoading, mutate } = usePartners()
  const [items, setItems] = useState<Partner[]>([])
  const [orderChanged, setOrderChanged] = useState(false)
  const [reordering, setReordering] = useState(false)
  useEffect(() => {
    const list: Partner[] = data?.data ?? []
    setItems([...list].sort((a, b) => (a.order ?? 0) - (b.order ?? 0)))
    setOrderChanged(false)
  }, [data])
  const moveUp = (index: number) => {
    if (index === 0) return
    setItems((prev) => { const next = [...prev]; [next[index - 1], next[index]] = [next[index], next[index - 1]]; return next })
    setOrderChanged(true)
  }
  const moveDown = (index: number) => {
    if (index === items.length - 1) return
    setItems((prev) => { const next = [...prev]; [next[index], next[index + 1]] = [next[index + 1], next[index]]; return next })
    setOrderChanged(true)
  }
  const saveOrder = () => confirmAction('Шинэ дарааллыг хадгалах уу? Энэ нь website дээр харагдах дарааллыг өөрчилнө.', async () => {
    setReordering(true)
    try {
      await api.admin.reorderPartners(items.map((i) => i._id))
      toast.success('Дараалал хадгалагдлаа')
      setOrderChanged(false); mutate()
    } catch (e: any) { toast.error(e instanceof Error ? e.message : 'Алдаа гарлаа') }
    finally { setReordering(false) }
  }, { title: 'Дараалал хадгалах', confirmLabel: 'Хадгалах' })

  const [open, setOpen] = useState(false)
  const [editId, setEditId] = useState<string | null>(null)
  const [uploading, setUploading] = useState(false)
  const { control, handleSubmit, reset, setValue, watch, formState: { errors, isSubmitting } } =
    useForm<PartnerFormData>({ defaultValues: partnerEmpty })
  const watchLogo = watch('logo')

  const create = () => { setEditId(null); reset(partnerEmpty); setOpen(true) }
  const edit = (i: Partner) => {
    setEditId(i._id)
    reset({ name: i.name, logo: i.logo, url: i.url, order: i.order })
    setOpen(true)
  }
  const close = () => { setOpen(false); reset(partnerEmpty) }
  const onSubmit = handleSubmit(async (form) => {
    try {
      if (editId) await api.admin.updatePartner(editId, form as unknown as Record<string, unknown>)
      else await api.admin.createPartner(form as unknown as Record<string, unknown>)
      close(); mutate()
    } catch (e: any) { toast.error(e instanceof Error ? e.message : 'Алдаа гарлаа') }
  })
  const remove = (id: string) => confirmDelete('Энэ бичлэгийг устгах уу?', async () => { await api.admin.deletePartner(id); mutate() })
  const toggle = async (i: Partner) => { await api.admin.updatePartnerStatus(i._id, !i.isActive); mutate() }
  const upload = async (file: File) => {
    setUploading(true)
    try { const r = await api.admin.uploadImage(file); setValue('logo', r.url, { shouldDirty: true }) }
    finally { setUploading(false) }
  }

  return (
    <Stack>
      <Group justify="space-between"><Text fw={500}>Хамтрагч ({items.length})</Text>
        <Group gap="xs">
          {orderChanged && (
            <Button size="xs" variant="light" color="orange" leftSection={<IconDeviceFloppy size={14} />} onClick={saveOrder} loading={reordering}>Дараалал хадгалах</Button>
          )}
          <Button size="xs" leftSection={<IconPlus size={14} />} onClick={create}>Нэмэх</Button>
        </Group>
      </Group>
      <div style={{ position: 'relative' }}>
        <LoadingOverlay visible={isLoading} />
        <Table striped>
          <Table.Thead><Table.Tr><Table.Th>Лого</Table.Th><Table.Th>Нэр</Table.Th><Table.Th>URL</Table.Th><Table.Th>Статус</Table.Th><Table.Th w={170}>Үйлдэл</Table.Th></Table.Tr></Table.Thead>
          <Table.Tbody>
            {items.map((i, idx) => (
              <Table.Tr key={i._id}>
                <Table.Td>{i.logo ? <img src={i.logo} alt="" style={{ height: 32, objectFit: 'contain' }} /> : '—'}</Table.Td>
                <Table.Td><Text size="sm" fw={600}>{i.name}</Text></Table.Td>
                <Table.Td><Text size="xs" c="dimmed">{i.url}</Text></Table.Td>
                <Table.Td><Badge color={i.isActive ? 'green' : 'gray'} variant="light" size="sm">{i.isActive ? 'Идэвхтэй' : 'Идэвхгүй'}</Badge></Table.Td>
                <Table.Td><Group gap={4}>
                  <Tooltip label="Дээш" position="top" withArrow><ActionIcon variant="subtle" color="gray" size="sm" disabled={idx === 0} onClick={() => moveUp(idx)}><IconArrowUp size={14} /></ActionIcon></Tooltip>
                  <Tooltip label="Доош" position="top" withArrow><ActionIcon variant="subtle" color="gray" size="sm" disabled={idx === items.length - 1} onClick={() => moveDown(idx)}><IconArrowDown size={14} /></ActionIcon></Tooltip>
                  <ActionIcon variant="light" size="sm" onClick={() => toggle(i)}>{i.isActive ? <IconEye size={14} /> : <IconEyeOff size={14} />}</ActionIcon>
                  <ActionIcon variant="light" size="sm" onClick={() => edit(i)}><IconPencil size={14} /></ActionIcon>
                  <ActionIcon variant="light" color="red" size="sm" onClick={() => remove(i._id)}><IconTrash size={14} /></ActionIcon>
                </Group></Table.Td>
              </Table.Tr>
            ))}
            {items.length === 0 && !isLoading && <Table.Tr><Table.Td colSpan={5}><Text ta="center" c="dimmed" py="lg">Хоосон</Text></Table.Td></Table.Tr>}
          </Table.Tbody>
        </Table>
      </div>
      <Modal opened={open} onClose={close} title={editId ? 'Засах' : 'Шинэ'} size="lg">
        <form onSubmit={onSubmit}>
          <Stack>
            <Controller name="name" control={control} rules={{ validate: (v) => v.trim() ? true : 'Нэр шаардлагатай' }} render={({ field }) => <TextInput label="Нэр *" {...field} error={errors.name?.message} />} />
            <Controller name="url" control={control} render={({ field }) => <TextInput label="URL" {...field} />} />
            <Controller name="order" control={control} render={({ field }) => <NumberInput label="Дараалал" value={field.value} onChange={(v) => field.onChange(Number(v) || 0)} min={0} />} />
            <div>
              <Text size="sm" fw={500} mb={4}>Лого</Text>
              <Group>
                <Controller name="logo" control={control} render={({ field }) => <TextInput flex={1} placeholder="URL" {...field} />} />
                <Button size="sm" variant="light" leftSection={<IconPhoto size={14} />} loading={uploading} onClick={() => pickFile(upload)}>Upload</Button>
              </Group>
              {watchLogo && <img src={watchLogo} alt="" style={{ marginTop: 8, maxHeight: 60 }} />}
            </div>
            <Group justify="flex-end"><Button variant="light" onClick={close} disabled={isSubmitting}>Болих</Button><Button type="submit" loading={isSubmitting} disabled={isSubmitting}>{editId ? 'Хадгалах' : 'Нэмэх'}</Button></Group>
          </Stack>
        </form>
      </Modal>
    </Stack>
  )
}
